import { useMemo } from 'react';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

export type SessionHomeworkStatus = 'upcoming' | 'no-exercises' | 'none' | 'partial' | 'full';

export type DotHomework = {
  status: SessionHomeworkStatus;
  submittedExercises: number;
  totalExercises: number;
};

/** The slice of a semester session the dots need (list, profile and group table all provide it). */
export type DotSession = {
  eventId: string;
  title?: string | null;
  eventDate: Date | string;
  classId?: string | null;
  className?: string | null;
  scorecardId?: string | null;
  isPast: boolean;
  /** null = no class record for this session yet. */
  isPresent: boolean | null;
  /** null = no class attached to the session. */
  homework: DotHomework | null;
};

export type DotKind = 'presence' | 'homework';
export type DotSize = 'sm' | 'md';

export type CommunityEventDot = { eventId: string; title: string | null; eventDate: Date | string; attended: boolean };

const SIZE_CLASS: Record<DotSize, string> = { sm: 'h-3 w-3', md: 'h-4 w-4' };

const COLORS = {
  present: 'bg-emerald-500',
  absent: 'bg-rose-400',
  noRecord: 'bg-gray-300',
  upcoming: 'border border-gray-300 bg-white',
  full: 'bg-emerald-500',
  partial: 'bg-amber-400',
  none: 'bg-rose-400',
  noExercises: 'bg-gray-300',
  noClass: 'border border-dotted border-gray-400 bg-white',
  empty: 'border border-gray-200 bg-white',
} as const;

const toDate = (value: Date | string) => (value instanceof Date ? value : new Date(value));

/** Tailwind classes for a presence dot. */
export function presenceDotClass(session: Pick<DotSession, 'isPast' | 'isPresent'>): string {
  if (session.isPresent === true) return COLORS.present;
  if (session.isPresent === false) return COLORS.absent;
  return session.isPast ? COLORS.noRecord : COLORS.upcoming;
}

/** Tailwind classes for a homework dot. */
export function homeworkDotClass(session: Pick<DotSession, 'homework'>): string {
  const homework = session.homework;
  if (!homework) return COLORS.noClass;
  switch (homework.status) {
    case 'full':
      return COLORS.full;
    case 'partial':
      return COLORS.partial;
    case 'none':
      return COLORS.none;
    case 'no-exercises':
      return COLORS.noExercises;
    case 'upcoming':
    default:
      return COLORS.upcoming;
  }
}

/** Translated tooltip for a dot. */
export function useDotTitle() {
  const { t } = useTranslation('admin');
  return (session: DotSession, kind: DotKind): string => {
    const date = toDate(session.eventDate).toLocaleDateString();
    if (kind === 'presence') {
      if (session.isPresent === true) return t('teacher.students.dot.present', { date });
      if (session.isPresent === false) return t('teacher.students.dot.absent', { date });
      return session.isPast ? t('teacher.students.dot.noRecord', { date }) : t('teacher.students.dot.upcoming', { date });
    }
    const className = session.className ?? session.title ?? t('common.fallbacks.class');
    const homework = session.homework;
    if (!homework) return t('teacher.students.dot.noClass', { date });
    const counts = { className, submitted: homework.submittedExercises, total: homework.totalExercises };
    switch (homework.status) {
      case 'full':
        return t('teacher.students.dot.homeworkFull', counts);
      case 'partial':
        return t('teacher.students.dot.homeworkPartial', counts);
      case 'none':
        return t('teacher.students.dot.homeworkNone', counts);
      case 'no-exercises':
        return t('teacher.students.dot.homeworkNoExercises', { className });
      case 'upcoming':
      default:
        return t('teacher.students.dot.homeworkUpcoming', { className });
    }
  };
}

/** Where the scorecard of a session opens (a new tab, so the teacher keeps her place). */
export function scorecardHref(session: Pick<DotSession, 'eventId' | 'scorecardId'>): string {
  return `/event/${session.eventId}/scorecard/${session.scorecardId ?? 'new'}`;
}

/**
 * One session as a dot. Presence dots open the class record in a new tab,
 * homework dots open the student's view of the class in the same tab.
 */
export function SessionDot({
  session,
  kind,
  studentId,
  size = 'md',
  linked = true,
}: {
  session: DotSession;
  kind: DotKind;
  studentId: string;
  size?: DotSize;
  linked?: boolean;
}) {
  const dotTitle = useDotTitle();
  const title = dotTitle(session, kind);
  const color = kind === 'presence' ? presenceDotClass(session) : homeworkDotClass(session);
  const base = `block shrink-0 rounded-full ${SIZE_CLASS[size]} ${color}`;
  const interactive = `${base} transition hover:ring-2 hover:ring-[#cf2e2e]/40`;

  if (linked && kind === 'presence') {
    return (
      <a
        href={scorecardHref(session)}
        target="_blank"
        rel="noopener"
        title={title}
        aria-label={title}
        className={interactive}
      />
    );
  }

  if (linked && kind === 'homework' && session.classId) {
    return (
      <Link
        to="/classes/$classId/student/$studentId"
        params={{ classId: session.classId, studentId }}
        search={{ teacherId: undefined, block: undefined }}
        title={title}
        aria-label={title}
        className={interactive}
      />
    );
  }

  return <span title={title} aria-label={title} role="img" className={base} />;
}

/**
 * A student's regular sessions of the semester, oldest first, padded with
 * empty outline dots so every row has at least `minSlots` dots.
 */
export function SemesterDots({
  sessions,
  kind,
  studentId,
  minSlots = 20,
  size = 'md',
  linked = true,
  className,
}: {
  sessions: DotSession[];
  kind: DotKind;
  studentId: string;
  minSlots?: number;
  size?: DotSize;
  linked?: boolean;
  className?: string;
}) {
  const ordered = useMemo(
    () => [...sessions].sort((a, b) => toDate(a.eventDate).getTime() - toDate(b.eventDate).getTime()),
    [sessions],
  );
  const padding = Math.max(0, minSlots - ordered.length);
  const gap = size === 'sm' ? 'gap-1' : 'gap-1.5';

  return (
    <div className={`flex flex-wrap items-center ${gap} ${className ?? ''}`}>
      {ordered.map((session, index) => (
        // A student can have two records for one event, so the index is part of the key.
        // eslint-disable-next-line react/no-array-index-key
        <SessionDot key={`${session.eventId}-${index}`} session={session} kind={kind} studentId={studentId} size={size} linked={linked} />
      ))}
      {Array.from({ length: padding }).map((_, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <span key={`empty-${index}`} aria-hidden className={`block shrink-0 rounded-full ${SIZE_CLASS[size]} ${COLORS.empty}`} />
      ))}
    </div>
  );
}

/** Community (extra) classes of the semester, oldest first: attended or not. */
export function ExtraClassDots({
  events,
  size = 'md',
  minSlots = 0,
  className,
}: {
  events: CommunityEventDot[];
  size?: DotSize;
  minSlots?: number;
  className?: string;
}) {
  const { t } = useTranslation('admin');
  const ordered = useMemo(
    () => [...events].sort((a, b) => toDate(a.eventDate).getTime() - toDate(b.eventDate).getTime()),
    [events],
  );
  const padding = Math.max(0, minSlots - ordered.length);
  const gap = size === 'sm' ? 'gap-1' : 'gap-1.5';

  return (
    <div className={`flex flex-wrap items-center ${gap} ${className ?? ''}`}>
      {ordered.map((event) => {
        const title = t(event.attended ? 'teacher.students.dot.extraAttended' : 'teacher.students.dot.extraMissed', {
          title: event.title ?? t('common.fallbacks.event'),
          date: toDate(event.eventDate).toLocaleDateString(),
        });
        return (
          <span
            key={event.eventId}
            title={title}
            aria-label={title}
            role="img"
            className={`block shrink-0 rounded-full ${SIZE_CLASS[size]} ${event.attended ? COLORS.present : COLORS.absent}`}
          />
        );
      })}
      {Array.from({ length: padding }).map((_, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <span key={`empty-${index}`} aria-hidden className={`block shrink-0 rounded-full ${SIZE_CLASS[size]} ${COLORS.empty}`} />
      ))}
    </div>
  );
}

/** What the colours mean, for one kind of row. */
export function DotLegend({ kind, className }: { kind: DotKind; className?: string }) {
  const { t } = useTranslation('admin');
  const items: Array<{ key: string; color: string }> =
    kind === 'presence'
      ? [
          { key: 'present', color: COLORS.present },
          { key: 'absent', color: COLORS.absent },
          { key: 'noRecord', color: COLORS.noRecord },
          { key: 'upcoming', color: COLORS.upcoming },
        ]
      : [
          { key: 'full', color: COLORS.full },
          { key: 'partial', color: COLORS.partial },
          { key: 'none', color: COLORS.none },
          { key: 'noExercises', color: COLORS.noExercises },
          { key: 'upcoming', color: COLORS.upcoming },
        ];
  return (
    <div className={`flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-gray-500 ${className ?? ''}`}>
      {items.map((item) => (
        <span key={item.key} className="inline-flex items-center gap-1">
          <span className={`inline-block h-2.5 w-2.5 rounded-full ${item.color}`} />
          {t(`teacher.students.legend.${item.key}`)}
        </span>
      ))}
    </div>
  );
}
